import type { PortalView } from "../types";

export interface Route {
  view: PortalView;
  /** Present only for the customer view. */
  customerGuid: string | null;
}

const VIEW_PATHS: Record<Exclude<PortalView, "overview" | "customer">, string> = {
  tickets: "tickets",
  "ticket-email-settings": "tickets/email-settings",
  users: "users",
  kyc: "kyc",
  transactions: "transactions",
  cards: "cards",
  "legacy-cards": "cards/legacy",
  "card-templates": "card-templates",
  logs: "logs",
  crashes: "logs/crashes",
  costs: "costs",
  audit: "audit",
  notifications: "notifications",
  controls: "controls",
  staff: "staff",
  settings: "settings"
};

const PATH_VIEWS = new Map<string, PortalView>(
  Object.entries(VIEW_PATHS).map(([view, path]) => [path, view as PortalView])
);

export function routeToPath({ view, customerGuid }: Route): string {
  if (view === "overview") return "/";
  if (view === "customer") return customerGuid ? `/customer/${encodeURIComponent(customerGuid)}` : "/users";
  return `/${VIEW_PATHS[view]}`;
}

export function pathToRoute(pathname: string): Route {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return { view: "overview", customerGuid: null };
  if (segments[0] === "logs" && segments[1] === "crashes") return { view: "crashes", customerGuid: null };
  if (segments[0] === "customer") {
    const guid = segments[1] ? decodeURIComponent(segments[1]) : null;
    return guid ? { view: "customer", customerGuid: guid } : { view: "users", customerGuid: null };
  }
  if (segments[0] === "cards" && segments[1] === "legacy") return { view: "legacy-cards", customerGuid: null };
  if (segments[0] === "tickets" && segments[1] === "email-settings") return { view: "ticket-email-settings", customerGuid: null };
  const view = PATH_VIEWS.get(segments[0]);
  return view ? { view, customerGuid: null } : { view: "overview", customerGuid: null };
}

export function currentRoute(): Route {
  return pathToRoute(window.location.pathname);
}

export function isSameRoute(left: Route, right: Route): boolean {
  return left.view === right.view && left.customerGuid === right.customerGuid;
}
